import { Controller } from 'react-hook-form';
import { IconLoader, Select, SelectSize, SelectType, Textarea } from '@ui/src';
import InfoRow from '../../common/info-row';
import StepPartBlock from '../../common/step-part-block';
import { NewApplicationStatus } from '../../../../hoc/common/types';
import { SourceOfFundsTypes } from './types';

import commonStyles from '../../common/styles.module.css';

const SourceOfFunds = ({
  questionnarieControl,
  customerSourceOfFunds,
  questionnarieClearErrors,
  questionnarieFields,
  questionnarieSetValue,
  status,
  isEditable,
  questionnarieErrors,
  questionnarieIsLoading,
}: SourceOfFundsTypes) => (
  <StepPartBlock title="Source of funds" zIndex={18}>
    {questionnarieIsLoading && (
      <div className={commonStyles.dictionaryLoader}>
        <IconLoader />
      </div>
    )}
    {!questionnarieIsLoading && (
      <>
        <InfoRow
          title="Source of funds to be credited to the account"
          id="sourcesOfFundsIds"
          style={{
            position: 'relative',
            zIndex: 10,
          }}
          lessPadding
        >
          <Controller
            control={questionnarieControl}
            name="sourcesOfFundsIds"
            defaultValue={[]}
            render={({ field: { onChange, value, name } }) => (
              <Select
                name={name}
                placeholder="Select an option..."
                list={
                  customerSourceOfFunds?.map((item) => ({
                    name: item.value,
                    value: item.id,
                  })) || []
                }
                value={value}
                size={SelectSize.SMALL}
                onChange={(e) => {
                  onChange(e);
                  questionnarieClearErrors(['sourcesOfFundsIds']);

                  if (
                    !(e.target.value as string[])?.includes('Other') &&
                    questionnarieFields?.sourcesOfFundsOther
                  ) {
                    questionnarieClearErrors(['sourcesOfFundsOther']);
                    questionnarieSetValue('sourcesOfFundsOther', '');
                  }

                  if (
                    !(e.target.value as string[])?.includes('Other') &&
                    questionnarieFields?.sourcesOfFundsLoanCreditInstitution
                  ) {
                    questionnarieClearErrors([
                      'sourcesOfFundsLoanCreditInstitution',
                    ]);
                    questionnarieSetValue(
                      'sourcesOfFundsLoanCreditInstitution',
                      '',
                    );
                  }

                  if (
                    !(e.target.value as string[])?.includes('Other') &&
                    questionnarieFields?.sourcesOfFundsLoanBusinessPartner
                  ) {
                    questionnarieClearErrors([
                      'sourcesOfFundsLoanBusinessPartner',
                    ]);
                    questionnarieSetValue(
                      'sourcesOfFundsLoanBusinessPartner',
                      '',
                    );
                  }
                }}
                selectWrapperClassName={
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED
                    ? commonStyles.selectDisabled
                    : ''
                }
                disabled={
                  !isEditable ||
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                }
                tabIndex={
                  !isEditable &&
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                    ? -1
                    : 0
                }
                error={questionnarieErrors?.sourcesOfFundsIds?.message}
                mode={SelectType.MULTIPLE}
                fullWidth
                borderless
              />
            )}
          />
        </InfoRow>
        {questionnarieFields?.sourcesOfFundsIds?.find((item) =>
          customerSourceOfFunds
            ?.find((client) => item === client.id)
            ?.value?.includes('Other'),
        ) && (
          <InfoRow
            title="Provide details"
            id="sourcesOfFundsOther"
            className={commonStyles.noFlex}
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="sourcesOfFundsOther"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Textarea
                  name={name}
                  title=""
                  placeholder="Enter details..."
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['sourcesOfFundsOther']);
                  }}
                  className={`borderless ${commonStyles.pepTextarea} ${commonStyles.stepOneTextarea}`}
                  error={questionnarieErrors?.sourcesOfFundsOther?.message}
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                />
              )}
            />
          </InfoRow>
        )}
        {questionnarieFields?.sourcesOfFundsIds?.find((item) =>
          customerSourceOfFunds
            ?.find((client) => item === client.id)
            ?.value?.includes('Loan from a credit institution'),
        ) && (
          <InfoRow
            title="Credit institution which have provided loan"
            id="sourcesOfFundsLoanCreditInstitution"
            className={commonStyles.noFlex}
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="sourcesOfFundsLoanCreditInstitution"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Textarea
                  name={name}
                  placeholder="Enter details..."
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors([
                      'sourcesOfFundsLoanCreditInstitution',
                    ]);
                  }}
                  className={`borderless ${commonStyles.pepTextarea} ${commonStyles.stepOneTextarea}`}
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={
                    questionnarieErrors?.sourcesOfFundsLoanCreditInstitution
                      ?.message
                  }
                />
              )}
            />
          </InfoRow>
        )}
        {questionnarieFields?.sourcesOfFundsIds?.find((item) =>
          customerSourceOfFunds
            ?.find((client) => item === client.id)
            ?.value?.includes('Loan from a business partner'),
        ) && (
          <InfoRow
            title="Business partner which provided loan"
            id="sourcesOfFundsLoanBusinessPartner"
            className={commonStyles.noFlex}
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="sourcesOfFundsLoanBusinessPartner"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Textarea
                  name={name}
                  placeholder="Enter details..."
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors([
                      'sourcesOfFundsLoanBusinessPartner',
                    ]);
                  }}
                  className={`borderless ${commonStyles.pepTextarea} ${commonStyles.stepOneTextarea}`}
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={
                    questionnarieErrors?.sourcesOfFundsLoanCreditInstitution
                      ?.message
                  }
                />
              )}
            />
          </InfoRow>
        )}
        <InfoRow
          title="Does the Customer have accounts in other financial institutions?"
          id="hasAccountsInOtherFinancialInstitutions"
          style={{
            position: 'relative',
            zIndex: 9,
          }}
          lessPadding
        >
          <Controller
            control={questionnarieControl}
            name="hasAccountsInOtherFinancialInstitutions"
            defaultValue=""
            render={({ field: { onChange, value, name } }) => (
              <Select
                name={name}
                placeholder="Select an option..."
                list={[
                  {
                    name: 'Yes',
                    value: 'yes',
                  },
                  {
                    name: 'No',
                    value: 'no',
                  },
                ]}
                value={value as string}
                size={SelectSize.SMALL}
                onChange={(e) => {
                  onChange(e);
                  questionnarieClearErrors([
                    'hasAccountsInOtherFinancialInstitutions',
                  ]);

                  if (
                    e.target.value === 'no' &&
                    questionnarieFields?.otherFinancialInstitutions
                  ) {
                    questionnarieClearErrors(['otherFinancialInstitutions']);
                    questionnarieSetValue('otherFinancialInstitutions', '');
                  }
                }}
                selectWrapperClassName={
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED
                    ? commonStyles.selectDisabled
                    : ''
                }
                disabled={
                  !isEditable ||
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                }
                tabIndex={
                  !isEditable &&
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                    ? -1
                    : 0
                }
                error={
                  questionnarieErrors?.hasAccountsInOtherFinancialInstitutions
                    ?.message
                }
                fullWidth
                borderless
              />
            )}
          />
        </InfoRow>
        {questionnarieFields?.hasAccountsInOtherFinancialInstitutions ===
          'yes' && (
          <InfoRow
            title="Financial institutions at which customer has accounts"
            id="otherFinancialInstitutions"
            className={commonStyles.noFlex}
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="otherFinancialInstitutions"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Textarea
                  name={name}
                  title=""
                  placeholder="Enter details..."
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['otherFinancialInstitutions']);
                  }}
                  className={`borderless ${commonStyles.pepTextarea} ${commonStyles.stepOneTextarea}`}
                  error={
                    questionnarieErrors?.otherFinancialInstitutions?.message
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                />
              )}
            />
          </InfoRow>
        )}
      </>
    )}
  </StepPartBlock>
);

export default SourceOfFunds;
